import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LineChart, Line, ResponsiveContainer } from "recharts";
import { ShieldCheck, FileBarChart, MessageSquare, Send, ArrowRight, TrendingUp } from "lucide-react";
import { api } from "@/lib/api";
import { SectionCard } from "@/components/Primitives";

const WINDOWS = [30, 90];

/**
 * Command Center summary tile for the Outcome Data Tracking System.
 * Shows headline KPIs across all 4 modules with mini sparklines + a 30/90d toggle.
 */
export default function OutcomeDashboard() {
  const navigate = useNavigate();
  const [window, setWindow] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/outcomes/summary?window=${window}`)
      .then(r => setData(r.data))
      .finally(() => setLoading(false));
  }, [window]);

  const tiles = data ? [
    {
      key: "prior-auth",
      title: "Prior Authorization",
      icon: ShieldCheck,
      color: "#4A0E8F",
      kpis: [
        { label: "Approval", value: `${data.prior_auth.approval_rate}%` },
        { label: "Avg Days", value: data.prior_auth.avg_days_to_decision },
        { label: "Volume", value: data.prior_auth.total },
      ],
      series: data.prior_auth.daily_series,
      testid: "od-pa",
    },
    {
      key: "cpt",
      title: "CPT Acceptance",
      icon: FileBarChart,
      color: "#0D7377",
      kpis: [
        { label: "Acceptance", value: `${data.cpt.acceptance_rate}%` },
        { label: "Match", value: `${data.cpt.code_match_rate}%` },
        { label: "Gap", value: `$${Math.round(data.cpt.revenue_gap).toLocaleString()}` },
      ],
      series: data.cpt.daily_series,
      testid: "od-cpt",
    },
    {
      key: "aria",
      title: "ARIA Queries",
      icon: MessageSquare,
      color: "#9B59B6",
      kpis: [
        { label: "Volume", value: data.aria.total_queries },
        { label: "Success", value: `${data.aria.success_rate}%` },
        { label: "👍 / 👎", value: `${data.aria.thumbs_up} / ${data.aria.thumbs_down}` },
      ],
      series: data.aria.daily_series,
      testid: "od-aria",
    },
    {
      key: "referrals",
      title: "Referrals",
      icon: Send,
      color: "#7E22CE",
      kpis: [
        { label: "Completion", value: `${data.referrals.completion_rate}%` },
        { label: "Scheduled", value: `${data.referrals.appointment_scheduled_rate}%` },
        { label: "F/U", value: `${data.referrals.patient_followup_rate}%` },
      ],
      series: data.referrals.daily_series,
      testid: "od-refs",
    },
  ] : [];

  return (
    <SectionCard
      title={<span className="flex items-center gap-2"><TrendingUp className="w-4 h-4 text-brand-primary" /> Outcome Dashboard</span>}
      testid="outcome-dashboard"
      actions={
        <div className="flex items-center gap-2">
          <div className="inline-flex border border-brand rounded-md overflow-hidden text-xs">
            {WINDOWS.map(w => (
              <button key={w} onClick={() => setWindow(w)}
                className={`px-3 py-1 transition-colors ${window === w ? "bg-brand-primary text-white" : "bg-white text-brand-muted hover:bg-brand-bg"}`}
                data-testid={`od-window-${w}`}>
                {w}-day
              </button>
            ))}
          </div>
          <button onClick={() => navigate(`/outcomes?window=${window}`)}
            className="text-xs px-3 py-1 rounded-md border border-brand hover:bg-brand-bg inline-flex items-center gap-1.5"
            data-testid="od-open-full">
            View full <ArrowRight className="w-3 h-3" />
          </button>
        </div>
      }>
      {loading ? (
        <div className="text-xs text-brand-muted py-6 text-center">Loading…</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
          {tiles.map(t => {
            const Ico = t.icon;
            return (
              <button key={t.key} onClick={() => navigate(`/outcomes?tab=${t.key}&window=${window}`)}
                className="group text-left p-3.5 rounded-lg border border-brand bg-white hover:shadow-md transition-all hover:border-brand-primary"
                data-testid={t.testid}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Ico className="w-4 h-4" style={{ color: t.color }} />
                    <div className="text-xs font-semibold text-brand-text">{t.title}</div>
                  </div>
                  <ArrowRight className="w-3.5 h-3.5 text-brand-muted opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
                <div className="grid grid-cols-3 gap-1.5 mb-2">
                  {t.kpis.map((k, i) => (
                    <div key={i}>
                      <div className="text-[10px] uppercase tracking-wider text-brand-muted">{k.label}</div>
                      <div className="text-sm font-semibold text-brand-text">{k.value}</div>
                    </div>
                  ))}
                </div>
                <div className="h-10" style={{ minHeight: 40 }}>
                  <ResponsiveContainer width="100%" height="100%" minHeight={40}>
                    <LineChart data={t.series}>
                      <Line type="monotone" dataKey="count" stroke={t.color} strokeWidth={1.5} dot={false} isAnimationActive={false} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </SectionCard>
  );
}
